import CharacterTracker from "../../characters/model/CharacterTracker";
import ConversationFeedback from "./ConversationFeedback";
import GameConversationMessage from "./GameConversationMessage";
import LlmConversationMessage from "./LlmConversationMessage";

export default class Conversation extends createjs.EventDispatcher {
    /**
     * @constructor
     * @param {string} id 
     * @param {string} chapterId
     * @param {number} maxPlayerTurns 
     */
    constructor(id, chapterId, maxPlayerTurns) {
        super();

        this._id = id;
        this._chapterId = chapterId;
        this._maxPlayerTurns = maxPlayerTurns;
        this._currentPhaseId = `phase_1`; // phase_1 = default phase.

        /** @type {LlmConversationMessage[]} */
        this._conversationHistoryForLLM = [];
        /** @type {GameConversationMessage[]} */
        this._conversationHistoryForGame = [];
        this._turnsByPhaseId = {};
        this._hasEnded = false;
        /** @type {ConversationFeedback[]} */
        this._feedback = [];
        this._feedbackSummary = "";
        /** @type {string} */
        this._outcome = null;

        /** @type {Object<string, CharacterTracker[]>} */
        this._startingTrackersByCharacterId = {};
        /** @type {Object<string, CharacterTracker[]>} */
        this._endingTrackersByCharacterId = {};
    }

    get id() {
        return this._id;
    }

    get chapterId() {
        return this._chapterId;
    }

    get maxPlayerTurns() {
        return this._maxPlayerTurns;
    }

    get currentPhaseId() {
        return this._currentPhaseId;
    }

    get turnsByPhaseId() {
        return this._turnsByPhaseId[this._currentPhaseId];
    }

    get conversationHistoryForLLM() {
        return this._conversationHistoryForLLM;
    }

    get conversationHistoryForGame() {
        return this._conversationHistoryForGame;
    }

    get hasEnded() {
        return this._hasEnded;
    }

    get feedback() {
        return this._feedback;
    }

    get feedbackSummary() {
        return this._feedbackSummary;
    }

    set feedbackSummary(value) {
        this._feedbackSummary = value;
    }

    get outcome() {
        return this._outcome;
    }

    end() {
        this._hasEnded = true;
    }

    /**
     * 
     * @param {Object} params
     * @param {LlmConversationMessage[]} params.llmConversationMessages
     * @param {GameConversationMessage[]} params.gameConversationMessages 
     */
    addConversationHistory({ llmConversationMessages, gameConversationMessages }) {
        this._conversationHistoryForLLM.push(...llmConversationMessages);
        this._conversationHistoryForGame.push(...gameConversationMessages);

        const newMessagesEvent = new createjs.Event(Conversation.EVENT_MESSAGES);
        newMessagesEvent.llmConversationMessages = llmConversationMessages;
        newMessagesEvent.gameConversationMessages = gameConversationMessages;
        this.dispatchEvent(newMessagesEvent);
    }

    transitionToNewPhase(phaseId) {
        this._currentPhaseId = phaseId;
        this._turnsByPhaseId[phaseId] = 0;
    }

    bumpTurnsForCurrentPhase() {
        this.bumpTurnsForPhaseId(this._currentPhaseId);
    }

    bumpTurnsForPhaseId(phaseId) {
        if (!this._turnsByPhaseId.hasOwnProperty(phaseId)) {
            this._turnsByPhaseId[phaseId] = 0;
        }

        this._turnsByPhaseId[phaseId]++;
    }

    getTurnsForCurrentPhase() {
        return this.getTurnsForPhaseId(this._currentPhaseId);
    }

    getTurnsForPhaseId(phaseId) {
        return this._turnsByPhaseId[phaseId] || 0;
    }

    getTotalTurns() {
        return Object.values(this._turnsByPhaseId).reduce((acc, turns) => acc + turns, 0) || 0;
    }

    setOutcome(outcome) {
        this._outcome = outcome;
    }

    setPhase(phaseId) {
        if (this._currentPhaseId === phaseId) {
            return;
        }

        const previousPhaseId = this._currentPhaseId;
        this._currentPhaseId = phaseId;

        const phaseChangedEvent = new createjs.Event(Conversation.EVENT_CHANGED_PHASE);
        phaseChangedEvent.previousPhaseId = previousPhaseId;
        phaseChangedEvent.phaseId = phaseId;
        this.dispatchEvent(phaseChangedEvent);
    }

    getHintLpKeyForCurrentPhase() {
        return `conversationHints.${this.chapterId}.${this.id}.${this._currentPhaseId}`;
    }

    /**
     * @param {ConversationFeedback} feedback 
     */
    addFeedback(feedback) {
        this._feedback.push(feedback);
    }
    
    /**
     * @param {ConversationFeedback[]} feedbacks
     */
    addFeedbacks(feedbacks) {
        for (const feedback of feedbacks) {
            this.addFeedback(feedback);
        }
    }

    /**
     * @param {string} characterId 
     * @param {CharacterTracker[]} trackers 
     */
    setStartingTrackersForCharacter(characterId, trackers) {
        this._startingTrackersByCharacterId[characterId] = trackers;
    }

    /**
     * @param {string} characterId 
     * @returns {CharacterTracker[]}
     */
    getStartingTrackersForCharacter(characterId) {
        return this._startingTrackersByCharacterId[characterId];
    }

    /**
     * @param {string} characterId 
     * @param {CharacterTracker[]} trackers 
     */
    setEndingTrackersForCharacter(characterId, trackers) {
        this._endingTrackersByCharacterId[characterId] = trackers;
    }

    /**
     * @param {string} characterId 
     * @returns {CharacterTracker[]}
     */
    getEndingTrackersForCharacter(characterId) {
        return this._endingTrackersByCharacterId[characterId];
    }

    toObject() {
        return {
            id: this._id,
            chapterId: this._chapterId,
            maxPlayerTurns: this._maxPlayerTurns,
            currentPhase: this._currentPhaseId,
            conversationHistoryForLLM: this._conversationHistoryForLLM.map((message) => message.toObject()),
            conversationHistoryForGame: this._conversationHistoryForGame.map((message) => message.toObject()),
            turnsByPhaseId: Object.assign({}, this._turnsByPhaseId), // NOTE: Shallow copy.
            hasEnded: this._hasEnded,
            feedback: this._feedback.map((feedback) => feedback.toObject()),
            feedbackSummary: this._feedbackSummary,
            outcome: this._outcome,
            startingTrackersByCharacterId: Object.entries(this._startingTrackersByCharacterId)?.reduce((acc, [characterId, trackers]) => {
                acc[characterId] = trackers.map((tracker) => tracker.toObject());
                return acc;
            }, []),
            endingTrackersByCharacterId: Object.entries(this._endingTrackersByCharacterId)?.reduce((acc, [characterId, trackers]) => {
                acc[characterId] = trackers.map((tracker) => tracker.toObject());
                return acc;
            }, []),
        };
    }

    load(data) {
        this._id = data.id;
        this._chapterId = data.chapterId;
        this._maxPlayerTurns = data.maxPlayerTurns;
        this._currentPhaseId = data.currentPhase;
        this._conversationHistoryForLLM = data.conversationHistoryForLLM.map((message) => LlmConversationMessage.fromObject(message));
        this._conversationHistoryForGame = data.conversationHistoryForGame.map((message) => GameConversationMessage.fromObject(message));
        this._turnsByPhaseId = data.turnsByPhaseId;
        this._hasEnded = data.hasEnded;
        this._feedback = data.feedback.map((feedback) => ConversationFeedback.fromObject(feedback));
        this._feedbackSummary = data.feedbackSummary;
        this._outcome = data.outcome;

        this._startingTrackersByCharacterId = Object.entries(data.startingTrackersByCharacterId)?.reduce((acc, [characterId, trackers]) => {
            acc[characterId] = trackers.map((tracker) => CharacterTracker.fromObject(tracker));
            return acc;
        }, []);

        this._endingTrackersByCharacterId = Object.entries(data.endingTrackersByCharacterId)?.reduce((acc, [characterId, trackers]) => {
            acc[characterId] = trackers.map((tracker) => CharacterTracker.fromObject(tracker));
            return acc;
        }, []);
    }
}

Conversation.EVENT_MESSAGES = 'messages';
Conversation.EVENT_CHANGED_PHASE = 'changedPhase';